import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ProblemStatement, MasterClass, PrizeBreakdown } from '../../catalogue/hackathon/hackathon.component';

const STRAPI_ENDPOINT = 'https://nha.gov.in/strapi/landing-page?_locale=en';

@Injectable({ providedIn: 'root' })
export class NhaContentService {
  constructor(private http: HttpClient) {}

  fetchHackathonContent(): Observable<{
    problemStatements: ProblemStatement[];
    masterClasses: MasterClass[];
  }> {
    return this.http.get<any>(STRAPI_ENDPOINT).pipe(
      map((data) => ({
        problemStatements: this.mapProblemStatements(data),
        masterClasses: this.mapMasterClasses(data),
      })),
      catchError(() => of({ problemStatements: [], masterClasses: [] }))
    );
  }

  // ── Problem Statements ────────────────────────────────────────────────────

  private mapProblemStatements(data: any): ProblemStatement[] {
    const raw: any[] =
      data?.problem_statements ??
      data?.problemStatements ??
      data?.hackathon_problem_statements ??
      data?.hackathonProblemStatements ??
      [];

    if (!Array.isArray(raw) || raw.length === 0) return [];

    return raw.map((ps: any, idx: number): ProblemStatement => ({
      id: ps.id ?? idx + 1,
      tag:
        ps.ps_number ??
        ps.psNumber ??
        ps.problemNumber ??
        ps.tag ??
        `PS-0${idx + 1}`,
      title: ps.title ?? '',
      description: ps.description ?? ps.summary ?? ps.shortDescription ?? '',
      difficulty: this.normaliseDifficulty(ps.difficulty ?? ps.level ?? 'Advanced'),
      domain: ps.domain ?? ps.category ?? ps.track ?? '',
      prizes: this.mapPrizes(ps),
      externalUrl:
        ps.url ??
        ps.external_url ??
        ps.externalUrl ??
        ps.link ??
        `https://nha.gov.in/problemStatement${idx + 1}`,
    }));
  }

  private normaliseDifficulty(raw: string): 'Beginner' | 'Intermediate' | 'Advanced' {
    const lower = (raw ?? '').toLowerCase();
    if (lower.includes('begin') || lower.includes('easy')) return 'Beginner';
    if (lower.includes('inter') || lower.includes('medium')) return 'Intermediate';
    return 'Advanced';
  }

  private mapPrizes(ps: any): PrizeBreakdown[] {
    if (Array.isArray(ps.prizes) && ps.prizes.length > 0) {
      return ps.prizes.map((p: any) => ({
        rank: p.rank ?? p.position ?? p.label ?? '',
        amount: p.amount ?? p.prize ?? p.value ?? '',
      }));
    }

    // fallback: flat prize fields (prize_1 / winner_1 / first_prize etc.)
    const fallback: PrizeBreakdown[] = [];
    const pairs: [string, string][] = [
      ['Winner 1', ps.prize_1 ?? ps.winner_1 ?? ps.firstPrize ?? ps.first_prize ?? ''],
      ['Winner 2', ps.prize_2 ?? ps.winner_2 ?? ps.secondPrize ?? ps.second_prize ?? ''],
      ['Winner 3', ps.prize_3 ?? ps.winner_3 ?? ps.thirdPrize ?? ps.third_prize ?? ''],
    ];
    pairs.forEach(([rank, amount]) => {
      if (amount) fallback.push({ rank, amount });
    });
    return fallback;
  }

  // ── Master Classes ────────────────────────────────────────────────────────

  private mapMasterClasses(data: any): MasterClass[] {
    const raw: any[] =
      data?.master_classes ??
      data?.masterClasses ??
      data?.hackathon_master_classes ??
      data?.hackathonMasterClasses ??
      [];

    if (!Array.isArray(raw) || raw.length === 0) return [];

    return raw.map((mc: any, idx: number): MasterClass => ({
      id: mc.id ?? idx + 1,
      title: mc.title ?? mc.session_title ?? mc.sessionTitle ?? '',
      speaker:
        mc.speaker ??
        mc.speaker_name ??
        mc.speakerName ??
        mc.presenter ??
        '',
      designation:
        mc.designation ??
        mc.speaker_designation ??
        mc.speakerDesignation ??
        mc.role ??
        '',
      organisation:
        mc.organisation ??
        mc.organization ??
        mc.speaker_org ??
        mc.speakerOrg ??
        mc.institute ??
        '',
      date: mc.date ?? mc.event_date ?? mc.eventDate ?? mc.session_date ?? '',
      time: mc.time ?? mc.event_time ?? mc.eventTime ?? mc.session_time ?? '',
      mode: mc.mode ?? mc.delivery_mode ?? mc.deliveryMode ?? 'In-Person',
      description: mc.description ?? mc.summary ?? mc.abstract ?? '',
      tags: this.mapTags(mc.tags ?? mc.topics ?? mc.categories ?? []),
    }));
  }

  private mapTags(raw: any): string[] {
    if (!Array.isArray(raw)) return [];
    return raw.map((t: any) =>
      typeof t === 'string' ? t : t?.name ?? t?.label ?? String(t)
    );
  }
}
